import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, errorText } from "@/lib/api";

export function useDesktop() {
  const q = "(min-width: 1024px)";
  const [d, setD] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = (e) => setD(e.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return d;
}

export function useStored(key, init) {
  const [v, setV] = useState(() => { try { const s = localStorage.getItem(key); return s == null ? init : JSON.parse(s); } catch { return init; } });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} }, [key, v]);
  return [v, setV];
}

export async function askCompass() {
  const D = window.DeviceOrientationEvent;
  if (D && typeof D.requestPermission === "function") { try { return (await D.requestPermission()) === "granted"; } catch { return false; } }
  return !!D;
}

export function useCompass(on) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!on) { setH(null); return undefined; }
    const ev = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
    const f = (e) => {
      const raw = e.webkitCompassHeading != null ? e.webkitCompassHeading : e.absolute && e.alpha != null ? 360 - e.alpha : null;
      if (raw == null) return;
      const v = Math.round((raw + (window.screen?.orientation?.angle || 0)) % 360);
      setH((p) => (p == null || Math.abs(p - v) >= 2 ? v : p));
    };
    window.addEventListener(ev, f, true);
    return () => window.removeEventListener(ev, f, true);
  }, [on]);
  return h;
}

export function useInstall() {
  const [evt, setEvt] = useState(null);
  const [installed, setInstalled] = useState(() => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true);
  useEffect(() => {
    const f = (e) => { e.preventDefault(); setEvt(e); };
    const g = () => { setInstalled(true); setEvt(null); };
    window.addEventListener("beforeinstallprompt", f);
    window.addEventListener("appinstalled", g);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
    return () => { window.removeEventListener("beforeinstallprompt", f); window.removeEventListener("appinstalled", g); };
  }, []);
  const install = useCallback(async () => {
    if (!evt) return false;
    evt.prompt();
    const r = await evt.userChoice.catch(() => ({}));
    setEvt(null);
    return r.outcome === "accepted";
  }, [evt]);
  return { installed, canPrompt: !!evt, ios: /iphone|ipad|ipod/i.test(navigator.userAgent), install };
}

export function useFavorites(on) {
  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!on) return;
    api.get("/driver/favorites").then(({ data }) => setList(data.favorites)).catch((e) => setErr(errorText(e, "Could not load saved spots.")));
  }, [on]);
  const ids = useMemo(() => new Set(list.map((f) => f.id)), [list]);
  const toggle = useCallback(async (s) => {
    try {
      if (ids.has(s.id)) {
        await api.delete("/driver/favorites", { params: { id: s.id } });
        setList((l) => l.filter((f) => f.id !== s.id));
      } else {
        await api.put("/driver/favorites", s);
        setList((l) => [{ ...s, saved_at: new Date().toISOString() }, ...l.filter((f) => f.id !== s.id)]);
      }
      setErr("");
    } catch (e) { setErr(errorText(e, "Could not update saved spots.")); }
  }, [ids]);
  return { list, ids, toggle, err };
}

export function usePlaceSearch(near) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const seq = useRef(0);
  const lat = near ? Math.round(near.lat * 1000) / 1000 : null, lon = near ? Math.round(near.lon * 1000) / 1000 : null;
  useEffect(() => {
    const t = q.trim();
    const id = ++seq.current;
    if (t.length < 2) { setRes([]); setErr(""); setBusy(false); return undefined; }
    setBusy(true);
    const h = setTimeout(() => {
      api.get("/driver/search", { params: { q: t, lat, lon } })
        .then(({ data }) => { if (id === seq.current) { setRes(data.results); setErr(""); } })
        .catch((e) => { if (id === seq.current) setErr(errorText(e, "Search failed.")); })
        .finally(() => { if (id === seq.current) setBusy(false); });
    }, 350);
    return () => clearTimeout(h);
  }, [q, lat, lon]);
  return { q, setQ, res, busy, err };
}
